/**
 * Offline-first write path (PRD "Instant" + "Survives network drops").
 *
 * Every tap becomes an Op that is (1) put in this in-memory queue — the UI re-renders
 * from it immediately via the optimistic overlay, well under 100 ms — (2) persisted to
 * IndexedDB so it survives a reload or a dead battery, and (3) flushed to POST /ops in
 * order, with retry and backoff while the network is down. The server applies each op
 * at most once (opId), so retries are always safe.
 */
import { createStore, get, set } from 'idb-keyval';
import type { GateStateDTO, Op, OpResult, OpsResponse } from '@clear/contracts';
import { api, ApiError, isNetworkError } from '../lib/api';
import { BASE } from '../lib/base';
import { syncServerTime } from '../lib/time';

export interface QueuedOp {
  op: Op;
  userId: string;
  /** Client-only data for the optimistic overlay (not sent): gate states to restore on undo. */
  prev?: Record<string, GateStateDTO | null>;
}

export interface SyncStatus {
  pending: number;
  online: boolean;
  syncing: boolean;
  /** Set while the server refuses our session (PIN needed); flushing resumes after login. */
  blockedByAuth: boolean;
  lastSyncAt: number | null;
}

export interface Rejection {
  op: Op;
  result: OpResult;
}

const BATCH = 50;
const ECHO_MS = 4_000;
const BACKOFF = [1_000, 2_000, 5_000, 10_000, 20_000, 30_000];

const idb = typeof indexedDB !== 'undefined' ? createStore(BASE ? 'clear-demo' : 'clear', 'outbox') : null;

class Outbox {
  private queue: QueuedOp[] = [];
  /**
   * Ops the server just accepted, kept in the overlay for a few seconds until the live
   * event (or refetch) carrying them lands — avoids a flicker back to the old state.
   */
  private echo: Array<{ q: QueuedOp; until: number }> = [];
  private status: SyncStatus = {
    pending: 0,
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
    syncing: false,
    blockedByAuth: false,
    lastSyncAt: null,
  };
  private listeners = new Set<() => void>();
  private rejectListeners = new Set<(r: Rejection[]) => void>();
  private appliedListeners = new Set<(ops: Op[]) => void>();
  private user: string | null = null;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private loaded: Promise<void> | null = null;
  // Stable snapshot references for useSyncExternalStore.
  private queueSnap: readonly QueuedOp[] = [];
  private statusSnap: SyncStatus = { ...this.status };

  load(): Promise<void> {
    if (!this.loaded) {
      this.loaded = (async () => {
        if (idb) {
          try {
            const saved = (await get<QueuedOp[]>('queue', idb)) ?? [];
            this.queue = [...saved, ...this.queue.filter((q) => !saved.some((s) => s.op.opId === q.op.opId))];
          } catch {
            /* storage unavailable: queue stays in memory */
          }
        }
        this.emit();
      })();
      if (typeof window !== 'undefined') {
        window.addEventListener('online', () => this.setOnline(true));
        window.addEventListener('offline', () => this.setOnline(false));
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') this.kick();
        });
      }
    }
    return this.loaded;
  }

  /** The logged-in user; only their ops are flushed (a shared phone may hold several people's taps). */
  setUser(userId: string | null): void {
    this.user = userId;
    this.status.blockedByAuth = false;
    this.emit();
    this.kick();
  }

  enqueue(q: QueuedOp): void {
    this.queue = [...this.queue, q];
    this.emit();
    void this.persist();
    this.kick();
  }

  ops(): readonly QueuedOp[] {
    return this.queueSnap;
  }

  getStatus(): SyncStatus {
    return this.statusSnap;
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  onRejected(fn: (r: Rejection[]) => void) {
    this.rejectListeners.add(fn);
    return () => {
      this.rejectListeners.delete(fn);
    };
  }

  /** Called with ops the server accepted — used to refresh views that cannot be patched locally. */
  onApplied(fn: (ops: Op[]) => void) {
    this.appliedListeners.add(fn);
    return () => {
      this.appliedListeners.delete(fn);
    };
  }

  /** Try to sync now (tap, reconnect, tab visible). */
  kick(): void {
    clearTimeout(this.timer);
    this.attempt = 0;
    void this.flush();
  }

  private setOnline(v: boolean) {
    this.status.online = v;
    this.emit();
    if (v) this.kick();
  }

  private emit() {
    this.status.pending = this.queue.length;
    const t = Date.now();
    this.echo = this.echo.filter((e) => e.until > t);
    this.queueSnap = this.echo.length ? [...this.echo.map((e) => e.q), ...this.queue] : this.queue;
    this.statusSnap = { ...this.status };
    this.listeners.forEach((fn) => fn());
  }

  private async persist() {
    if (!idb) return;
    try {
      await set('queue', this.queue, idb);
    } catch {
      /* ignore quota errors; the in-memory queue still syncs */
    }
  }

  private schedule() {
    clearTimeout(this.timer);
    const wait = BACKOFF[Math.min(this.attempt, BACKOFF.length - 1)]!;
    this.attempt += 1;
    this.timer = setTimeout(() => void this.flush(), wait);
  }

  async flush(): Promise<void> {
    await this.load();
    if (this.status.syncing || !this.user || this.status.blockedByAuth) return;
    const mine = this.queue.filter((q) => q.userId === this.user).slice(0, BATCH);
    if (!mine.length) return;
    this.status.syncing = true;
    this.emit();
    const sentAt = Date.now();
    try {
      const res = await api<OpsResponse>('/ops', { body: { ops: mine.map((q) => q.op) } });
      syncServerTime(res.serverTime, sentAt);
      const done = new Set(res.results.map((r) => r.opId));
      const rejected: Rejection[] = [];
      const applied: Op[] = [];
      for (const r of res.results) {
        const q = mine.find((m) => m.op.opId === r.opId);
        if (!q) continue;
        if (r.status === 'rejected') rejected.push({ op: q.op, result: r });
        else applied.push(q.op);
      }
      const until = Date.now() + ECHO_MS;
      this.echo = [
        ...this.echo,
        ...mine.filter((q) => applied.some((a) => a.opId === q.op.opId)).map((q) => ({ q, until })),
      ];
      setTimeout(() => this.emit(), ECHO_MS + 50);
      this.queue = this.queue.filter((q) => !done.has(q.op.opId));
      this.status.online = true;
      this.status.lastSyncAt = Date.now();
      this.attempt = 0;
      this.status.syncing = false;
      this.emit();
      await this.persist();
      if (rejected.length) this.rejectListeners.forEach((fn) => fn(rejected));
      if (applied.length) this.appliedListeners.forEach((fn) => fn(applied));
      if (this.queue.some((q) => q.userId === this.user)) void this.flush();
    } catch (e) {
      this.status.syncing = false;
      if (e instanceof ApiError && e.status === 401) {
        this.status.blockedByAuth = true;
      } else if (e instanceof ApiError && e.status === 400) {
        // A malformed op (should never happen) must not block the queue forever.
        const bad = mine[0]!;
        this.queue = this.queue.filter((q) => q !== bad);
        this.rejectListeners.forEach((fn) =>
          fn([{ op: bad.op, result: { opId: bad.op.opId, status: 'rejected', code: 'invalid' } }]),
        );
        void this.persist();
      } else {
        if (isNetworkError(e)) this.status.online = false;
        this.schedule();
      }
      this.emit();
    }
  }

  /** Test hook. */
  reset(): void {
    this.queue = [];
    this.echo = [];
    this.user = null;
    this.status = { pending: 0, online: true, syncing: false, blockedByAuth: false, lastSyncAt: null };
    this.emit();
  }
}

export const outbox = new Outbox();
